import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';

const DEFAULT_THRESHOLDS = {
  redMissingDays: 3,
  redStorePct: 90,
  redAnomalyCount: 5,
  yellowMissingDays: 1,
  yellowStorePct: 100,
  storeAnomalyMinMedian: 10,
  storeAnomalyRatio: 0.2,
  dowAnomalyMinAvg: 100,
  dowAnomalyRatio: 0.5,
  histWeeksBack: 4,
};

const STATUS_COLORS = {
  red: '#c62828',
  yellow: '#f9a825',
};

const FIELDS = [
  {
    section: 'Status Thresholds',
    fields: [
      { key: 'redMissingDays', label: 'Red: Missing days', type: 'int', color: STATUS_COLORS.red, helperText: 'Number of missing days to trigger critical status' },
      { key: 'redStorePct', label: 'Red: Store presence %', type: 'int', color: STATUS_COLORS.red, helperText: 'Below this % of stores present triggers critical' },
      { key: 'redAnomalyCount', label: 'Red: Anomaly count', type: 'int', color: STATUS_COLORS.red, helperText: 'Number of anomalies to trigger critical status' },
      { key: 'yellowMissingDays', label: 'Yellow: Missing days', type: 'int', color: STATUS_COLORS.yellow, helperText: 'Number of missing days to trigger warning status' },
      { key: 'yellowStorePct', label: 'Yellow: Store presence %', type: 'int', color: STATUS_COLORS.yellow, helperText: 'Below this % of stores present triggers warning' },
    ],
  },
  {
    section: 'Store Anomaly Detection',
    fields: [
      { key: 'storeAnomalyMinMedian', label: 'Min median row count', type: 'int', helperText: 'Store must have at least this median to flag anomalies' },
      { key: 'storeAnomalyRatio', label: 'Ratio (as %)', type: 'pct', helperText: 'Row count below this % of median flags an anomaly' },
    ],
  },
  {
    section: 'Day-of-Week Detection',
    fields: [
      { key: 'dowAnomalyMinAvg', label: 'Min avg row count', type: 'int', helperText: 'Day-of-week must have at least this average to flag anomalies' },
      { key: 'dowAnomalyRatio', label: 'Ratio (as %)', type: 'pct', helperText: 'Daily total below this % of day-of-week average flags an anomaly' },
    ],
  },
  {
    section: 'Historical Lookback',
    fields: [
      { key: 'histWeeksBack', label: 'Weeks back', type: 'int', helperText: 'Number of weeks of history used for day-of-week averages' },
    ],
  },
];

const ThresholdSettingsDialog = ({ open, onClose, categoryKey, categoryLabel, firebaseUser }) => {
  const [values, setValues] = useState({ ...DEFAULT_THRESHOLDS });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (!open || !categoryKey) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.get(`/hd-api/data-integrity-2/thresholds/${categoryKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setValues(res.data.thresholds);
          setUpdatedBy(res.data.updatedBy);
          setUpdatedAt(res.data.updatedAt);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading thresholds:', err);
          setError('Failed to load thresholds.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, categoryKey, firebaseUser]);

  const handleChange = (key, type) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setValues((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    if (type === 'pct') {
      const pct = parseFloat(raw);
      if (!isNaN(pct)) setValues((prev) => ({ ...prev, [key]: pct / 100 }));
    } else {
      const num = parseInt(raw, 10);
      if (!isNaN(num)) setValues((prev) => ({ ...prev, [key]: num }));
    }
  };

  const getDisplayValue = (key, type) => {
    const v = values[key];
    if (v === '' || v == null) return '';
    if (type === 'pct') return Math.round(v * 100);
    return v;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);
      await axios.put(`/hd-api/data-integrity-2/thresholds/${categoryKey}`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaving(false);
      onClose();
    } catch (err) {
      console.error('Error saving thresholds:', err);
      setError('Failed to save thresholds.');
      setSaving(false);
    }
  };

  const handleReset = () => {
    setValues({ ...DEFAULT_THRESHOLDS });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Lato', fontWeight: 600 }}>
        {categoryLabel} — Threshold Settings
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {FIELDS.map(({ section, fields }) => (
              <Box key={section}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  {section}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {fields.map(({ key, label, type, helperText, color }) => (
                    <TextField
                      key={key}
                      label={label}
                      type="number"
                      size="small"
                      value={getDisplayValue(key, type)}
                      onChange={handleChange(key, type)}
                      helperText={helperText}
                      sx={{
                        flex: '1 1 200px',
                        ...(color && {
                          '& .MuiOutlinedInput-root': {
                            borderLeft: `3px solid ${color}`,
                            '&:hover fieldset': { borderColor: color },
                            '&.Mui-focused fieldset': { borderColor: color },
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color },
                        }),
                      }}
                      InputProps={type === 'pct' ? { endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', ml: 0.5 }}>%</Typography> } : undefined}
                    />
                  ))}
                </Box>
              </Box>
            ))}

            {updatedBy && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                Last updated by {updatedBy}
                {updatedAt && ` on ${new Date(updatedAt).toLocaleString()}`}
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleReset} color="inherit" disabled={loading || saving}>
          Reset to Defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThresholdSettingsDialog;
